import { useMemo } from 'react';
import { CartPromoCodeDiscount, Tier } from '../components/TieredDiscount/types';
import { formatCurrencyRound } from '@utils/formatters.util';
import { Maybe } from '@types';

const getDiscountText = (discount: Maybe<CartPromoCodeDiscount>) => {
  if (!discount) return null;

  if (discount.discountType === 'percent') {
    return `${discount.discount * 100}% OFF`;
  }

  if (discount.discountType === 'orderAmount') {
    return `${formatCurrencyRound(discount.discount)} OFF`;
  }

  if (discount.discountType === 'freeShipping') {
    return 'free shipping';
  }

  return null;
};

type Props = {
  data: any;
  options?: {
    maxVisibleTiers?: number;
  };
};

export const useTieredDiscount = (data: any, options: Props['options'] = { maxVisibleTiers: 5 }) => {
  const { maxVisibleTiers = 5 } = options;
  const sortedDiscounts = useMemo(() => {
    const discounts = data?.promoCode?.discounts || [];
    const freeShipping = data?.summary?.freeShippingSubtotal;

    return [
      ...discounts,
      ...(!isNaN(freeShipping) && freeShipping > 0
        ? [{ subtotalThreshold: freeShipping, discountType: 'freeShipping' }]
        : []),
    ].sort((a, b) => a.subtotalThreshold - b.subtotalThreshold);
  }, [data]);

  const allTiers: Tier[] = useMemo(() => {
    if (!sortedDiscounts.length) return [];

    const tieredDiscounts = sortedDiscounts
      .map((discount) => ({
        threshold: discount.subtotalThreshold,
        gift: getDiscountText(discount),
      }))
      .filter(Boolean);

    return [...(sortedDiscounts.length < maxVisibleTiers ? [{ threshold: 0 }] : []), ...tieredDiscounts];
  }, [sortedDiscounts]);

  // Calculate sliding window of tiers to display (max 5)
  const tiers: Tier[] = useMemo(() => {
    if (allTiers.length <= maxVisibleTiers) return allTiers;

    const currentSubtotal = data?.summary?.subtotal || 0;

    const currentTierIndex = allTiers.findLastIndex((tier) => tier.threshold <= currentSubtotal);

    const isLastTier = currentTierIndex === allTiers.length - 1;
    if (isLastTier) return allTiers.slice(allTiers.length - maxVisibleTiers, allTiers.length);

    const nextTierIndex = currentTierIndex + 1;
    if (nextTierIndex >= maxVisibleTiers)
      return allTiers.slice(nextTierIndex - maxVisibleTiers + 1, nextTierIndex + 1);

    return allTiers.slice(0, maxVisibleTiers);
  }, [allTiers, data?.summary?.subtotal, maxVisibleTiers]);

  // Calculate current progress and active discount
  const { currentProgress, currentSubtotal, nextTierInfo } = useMemo(() => {
    const currentSubtotal = data?.summary?.subtotal || 0;

    if (!sortedDiscounts.length || !tiers.length) {
      return { currentProgress: 0, activeDiscount: null, nextTierInfo: null };
    }

    // Find current tier index and next tier within visible tiers
    let currentTierIndex = -1;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (currentSubtotal >= tiers[i].threshold) {
        currentTierIndex = i;
        break;
      }
    }

    const currentTier = currentTierIndex >= 0 ? tiers[currentTierIndex] : null;
    const nextTier = tiers[currentTierIndex + 1];

    // Calculate progress percentage within visible tiers
    let progress = 0;
    if (nextTier) {
      const currentThreshold = currentTier?.threshold || 0;
      const nextThreshold = nextTier.threshold;
      const progressInTier = (currentSubtotal - currentThreshold) / (nextThreshold - currentThreshold);
      progress = currentTierIndex >= 0 ? ((currentTierIndex + progressInTier) / (tiers.length - 1)) * 100 : 0;
    } else {
      // Check if there are more tiers in allTiers beyond visible window
      const hasMoreTiers = allTiers.some((tier) => tier.threshold > currentSubtotal);
      if (hasMoreTiers) {
        // Find the actual next tier in allTiers
        const actualNextTier = allTiers.find((tier) => tier.threshold > currentSubtotal);
        if (actualNextTier) {
          // Show progress towards the hidden tier
          const lastVisibleTier = tiers[tiers.length - 1];
          const progressInTier =
            (currentSubtotal - lastVisibleTier.threshold) /
            (actualNextTier.threshold - lastVisibleTier.threshold);
          progress = Math.min(100, ((tiers.length - 1 + progressInTier) / (tiers.length - 1)) * 100);
        } else {
          progress = 100;
        }
      } else {
        // Cart exceeds highest tier
        progress = 100;
      }
    }

    // For nextTierInfo, check both visible tiers and all tiers
    let nextTierInfo = null;
    if (nextTier) {
      nextTierInfo = {
        threshold: nextTier.threshold,
        amountNeeded: nextTier.threshold - currentSubtotal,
        gift: nextTier.gift,
      };
    } else {
      // Look for next tier in allTiers that's beyond visible window
      const actualNextTier = allTiers.find((tier) => tier.threshold > currentSubtotal);
      if (actualNextTier) {
        nextTierInfo = {
          threshold: actualNextTier.threshold,
          amountNeeded: actualNextTier.threshold - currentSubtotal,
          gift: actualNextTier.gift,
        };
      }
    }

    return {
      currentProgress: Math.min(Math.max(progress, 0), 100),
      currentSubtotal,
      nextTierInfo,
    };
  }, [data?.summary?.subtotal, sortedDiscounts, tiers, allTiers]);

  return {
    isVisible: data?.promoCode?.canMeter || !sortedDiscounts.length,
    currentProgress,
    nextTierInfo,
    tiers,
    currentSubtotal,
  };
};
